import {Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {ActivatedRoute, Router} from '@angular/router';
import {AlertPanelComponent, AlertPanelService} from "@amlCore/components";
import {DictionaryService, UserService, ResourceService, ValidatorService} from "@amlCore/services";
import {CheckTransaction} from "../../models";
import {DossierAccessEnum} from "../../../core/enum";
import {User} from "../../../core/models";
import {ExecutionTransferWindowComponent} from "../executionTransfer";
import {
  AbstractControl,
  FormArray,
  FormGroup
} from "@angular/forms";
import {forkJoin, Subject} from "rxjs";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {ExecutorDataModel} from "../../models/executorData.model";
import {Utils} from "@amlCore/utils";
import {finalize} from "rxjs/operators";
import {GenerateFesComponent} from "./generateFes";
import {CheckTransactionCode} from "../../models/transaction/checkTransactionCode.model";
import {AlertModalComponent, ConfirmComponent} from "@amlCore/modals";
import {CheckTransactionsService} from "./checkTransactions.service";
import {DictionariesService} from "../../../dictionaries";

@Component({
  selector: "app-check-transaction-form",
  templateUrl: "./checkTransactionForm.component.html",
  styleUrls: ['./checkTransactionForm.component.scss'],
})

export class CheckTransactionFormComponent implements OnInit, OnDestroy {
  @ViewChild("alertObjSave") alertObjToSave: AlertPanelComponent;

  private id: string; // уникальный идентификатор анализа операции
  public formCheck: CheckTransaction; // модель формы просмотра
  public isReadOnlyAccess = false;
  public canEditForm = false;
  isLoaded = false; // Признак, что форма загружена
  checkResult = null;
  disabled = false;
  submitted = false;
  dictLoaded = false;
  basicCode = null;
  /*справочники*/
  dictionary: { [name: string]: Array<any>; } = {};
  public usersWithPermissions: User[];
  private modal;
  viewForm: FormGroup;
  protected showSubjects: boolean = false;
  protected listRejected: CheckTransactionCode[] = [];
  $destroy = new Subject();
  constructor(
    private route: ActivatedRoute,
    protected router: Router,
    private resourceSrv: ResourceService,
    protected alertPanelSrv: AlertPanelService,
    protected userSrv: UserService,
    protected dictSrv: DictionaryService,
    private modalService: NgbModal,
    private validationSrv: ValidatorService,
    private service: CheckTransactionsService) {

  }

  ngOnInit() {
    this.isReadOnlyAccess = this.userSrv.getAccess(DossierAccessEnum.TRANSACTIONCHECKLIST).readOnly;
    this.loadRemoteData(['affiliationpdl', 'oksm', 'codeMessage4936U', 'resultchecking', 'typesubjects',
      'qrefVo4936', 'qrefPrpod5599', 'criticallists']);

    this.route.params.subscribe((params) => {
      this.id = params.id;
      this.loadCheckTransaction();
      this.resourceSrv.getUsersByTransactionCheck(this.id).subscribe((users) => {
        this.usersWithPermissions = users;
      });
    });
  }

  loadRemoteData(types: Array<string>) {
    const obList = types.map(type => this.dictSrv.getDictionaryData(type));
    forkJoin(obList).subscribe((data) => {
      types.forEach((type, index) => {
        this.dictionary[type] = data[index];
      });
      this.dictLoaded = true;
    });
  }

  createForm(data: CheckTransaction): void{
    this.viewForm = this.service.createTransactionCheckForm(data);
    this.updateForm(data, true);
  }

  /*Загрузка проверки по операции*/
  private loadCheckTransaction() {
    this.service.getCheckTransactionById(this.id).subscribe((data: CheckTransaction) => {
      this.createForm(data);
    });
  }

  initEditMode(): void {
    if(!this.isReadOnlyAccess){
      this.isLoaded = null;
      this.service.startEditCheckTransaction(this.id).pipe(finalize(()=> this.isLoaded = true)).subscribe(response => {
        if(!response?.result) {
          this.isReadOnlyAccess = true;
          this.canEditForm = false;
          this.viewForm.disable();
          this.alertObjToSave.open(
            this.alertPanelSrv.getErrorMsg(response?.message ?? "Ошибка редактирования документа", false)
          );
        }
      });
    }
  }

  public getClientInfo(type: string) {
    return this.formCheck.transaction[type].name;
  }

  /*Сохранить результаты проверки операции*/
  public saveCheckTransaction() {
    this.submitted = true;
    if(!this.viewForm.valid){
      return;
    }
    this.submitted = false;
    this.disabled = true;
    const editableData = Utils.clone(this.viewForm.value);
    editableData.id = this.formCheck.id;
    this.service.saveCheckTransaction(editableData).subscribe((data) => {
      this.disabled = false;
      this.updateForm(data);
      this.viewForm.markAsPristine();
      this.alertObjToSave.open(this.alertPanelSrv.getSuccessMsg('Данные успешно сохранены'));
    });
  }

  private _sendNoticeToUserEmail(data: ExecutorDataModel) {
    const body = { userId: data.userId, executionDate: data.date };
    const transactionId = this.formCheck.id;
    this.service.checkTransactionExecutor(body, transactionId).subscribe(data => {
      this.updateForm(data);
      this?.modal?.componentInstance?.afterSave();
    });
  }

  /*Продолжить проверку*/
  public continueCheckTransaction() {
    this.service.continueCheckTransaction(this.id).subscribe((data) => {
      this.updateForm(data);
      this.alertObjToSave.open(this.alertPanelSrv.getSuccessMsg("Проверка успешно возобновлена"));
      if(!this.basicCode){
        const modal = this.modalService.open(AlertModalComponent);
        modal.componentInstance.text = "Необходимо указать код/коды, по которым необходимо возобновить проверку";
      }
    });
  }

  /*Завершить проверку по операции*/
  public finishCheckTransaction() {
    this.service.finishCheckTransaction(this.id).subscribe((data) => {
      this.updateForm(data);
      this.alertObjToSave.open(this.alertPanelSrv.getSuccessMsg("Проверка завершена"));
    });
  }

  /*Просмотр формы транзакции*/
  public viewTransactionForm(transactionId: string) {
    // @ts-ignore
    this.router.navigate([`clientTransactionForm/${transactionId}`, {whereOpenUrl: "/cabinet/check-transactions".replaceAll('\/', '%2F')}]);
  }

  getExecutorData(): ExecutorDataModel {
    return {
      userId: this.formCheck.executorId,
      date: this.formCheck.executionDate
    };
  }

  /**
   * Передать на исполнение
   */
  changeExecutor(): void {
    this.modal = this.modalService.open(ExecutionTransferWindowComponent, {
      backdrop: 'static',
      keyboard: false,
      size: 'xl',
      scrollable: true
    });
    // Передаем данные в компонент
    this.modal.componentInstance.data = this.getExecutorData();
    this.modal.componentInstance.usersList = this.usersWithPermissions;
    this.modal.componentInstance.onSave.subscribe(value => {
      this._sendNoticeToUserEmail(value);
    });
  }

  updateForm(data?: CheckTransaction, isInit?: boolean): void{
    if(data){
      this.viewForm.patchValue(this.checkDataToModel(data));
      this.formCheck = data;
      this.basicCode = data.basicCode;
      this.showSubjects = data.checkCodes.some(c => c?.subjects?.length > 0);
      this.updateRejected();
    }
    this.checkCanEditForm();
    if(isInit) {
      this.initEditMode();
    }
    if(this.formCheck.result || !this.canEditForm){
      this.viewForm.disable();
    } else{
      this.viewForm.enable();
    }
  }

  checkDataToModel(data: CheckTransaction){
    return Utils.clone(data);
  }

  codesSearchFn() {
    return Utils.searchByCodeOrName();
  }

  unusualSearchFn() {
    return Utils.searchByFieldsFn(['code', 'briefdesc']);
  }

  /**
   * метод получения описания Принятые меры
   */
  protected getCurrCashTitle(): string {
    const value = this.viewForm.get('penalty').value;
    if (value) {
      return this.dictionary.codeMessage4936U?.find((item: any) => item.code === value)?.name || '';
    }
    return '';
  }

  /**
   * Сформировать ФЭС
   */
  public generateFes() {
    const ids = [this.formCheck.id];
    const modalRef = this.modalService.open(GenerateFesComponent,  {
      size: 'lg',
      backdrop: 'static',
      centered: true});
    modalRef.componentInstance.open(ids);
    modalRef.result.then(() => {
      this.loadCheckTransaction();
    });
  }

  private checkCanEditForm(): void {
    this.canEditForm = !this.isReadOnlyAccess && (!this?.formCheck?.executorId || this.formCheck?.executorId == this.userSrv.getCurrentUser()?.id);
  }

  fArray(form: FormGroup, field: string): FormArray {
    return form.get(field) as FormArray;
  }

  fGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  /**
   * Отклонить основной код
   */
  rejectBasicCode(): void {
    const basicCodeControl = this.viewForm.get('basicCode')
    basicCodeControl.markAsTouched();
    const code = this.basicCode;
    this.checkConfirmReject(code)
      .then(() => this.service.getRejectReason(code))
      .then((rejectReason: string) => {
        this._onCodeReject(code, rejectReason);
      }).catch(() => {
        basicCodeControl.setValue(this.basicCode);
        return;
      });
  }

  /**
   * Отклонить дополнительный код
   */
  rejectOtherCode($event: any): void {
    this.viewForm.get('otherCodes').markAsTouched();
    const code = $event.value.code;
    this.checkConfirmReject(code)
      .then(() => this.service.getRejectReason(code))
      .then((rejectReason: string) => {
        this._onCodeReject(code, rejectReason);
      }).catch(() => this.updateOtherCodes());
  }

  /**
   * Отклонить код необычной операции
   */
  rejectUnusualCode($event: any): void {
    this.viewForm.get('unusualCodes').markAsTouched();
    const code = $event.value.code;
    this.checkConfirmReject(code)
      .then(() => this.service.getRejectReason(code))
      .then((rejectReason: string) => {
        this._onUnusualCodeReject(code, rejectReason);
      }).catch(() => this.updateUnusualCodes());
  }
  
  private checkConfirmReject(code: number): Promise<any> {
    const codesFormArray = this.viewForm.get('checkCodes') as FormArray;
    if(code == 6001) {
      if(codesFormArray.controls.some(c => c.value.unusual && c.value.participantType == 3)) {
        return this.confirmRejectCode();
      }
    } else if(codesFormArray.controls.some(c => c.value.checkCode == code && c.value.participantType == 3)) {
      return this.confirmRejectCode();
    }
    return Promise.resolve();
  }

  private confirmRejectCode(): Promise<any>{
    const modal = this.modalService.open(ConfirmComponent);
    modal.componentInstance.text = 'Вы точно хотите отклонить код/коды на стороне Плательщика и Получателя?';
    return modal.result;
  }

  private _onCodeReject(code: number, rejectReason: string) {
    const codesFormArray = this.viewForm.get('checkCodes') as FormArray;
    if(code == 6001){
      this.viewForm.get('unusualCodes').setValue([]);
      this.onRejectCode6001();
      const unusualCodeItems = codesFormArray.controls.filter(c => c.value.unusual);
      for(const rejectedCodeItem of unusualCodeItems){
        this.setResultRejected(rejectedCodeItem, rejectReason);
      }
    } else {
      const codeItemForReject = codesFormArray.controls.find(c => c.value.checkCode == code);
      this.setResultRejected(codeItemForReject, rejectReason);
    }
    if(this.basicCode == code){ //при отклонении основного кода, нужно назначить новый
      this.setNewBasicCode();
    }
    this.updateRejected();
  }
  
  private setResultRejected(rejectedCodeItem: AbstractControl, rejectReason: string){
    rejectedCodeItem.get('basic').setValue(false);
    rejectedCodeItem.get('rejected').setValue(true);
    rejectedCodeItem.get('rejectReason').setValue(rejectReason);
    const subjects = rejectedCodeItem.get('subjects') as FormArray;
    subjects?.controls?.forEach(subject => {
      subject.get("checkResult").setValue("0");
    });
  }

  private revertRejected(codeItem: AbstractControl, revertSubjects?: boolean){
    codeItem.get('rejected').setValue(false);
    codeItem.get('rejectReason').setValue("");
    if(revertSubjects !== false){
      const subjects = codeItem.get('subjects') as FormArray;
      subjects?.controls?.forEach(subject => {
        subject.get("checkResult").setValue("1");
      });
    }
  }

  private _onUnusualCodeReject(code: number, rejectReason: string) {
    const codesFormArray = this.viewForm.get('checkCodes') as FormArray;
    const rejectedCodeItem = codesFormArray.controls.find(c => c.value.checkCode == code);
    if(!(this.viewForm.get('unusualCodes').value?.length > 0)){ //если других необычных кодов не осталось, нужно убрать 6001
      this.onRejectCode6001();
    }
    this.setResultRejected(rejectedCodeItem, rejectReason);
    this.updateRejected();
  }

  updateRejected(){
    const checkCodesArray =  this.fArray(this.viewForm, 'checkCodes');
    this.listRejected = checkCodesArray.getRawValue().filter(c => c.rejected)
      .sort((a, b) => {
        const main = this.getMainCheckCode(a) - this.getMainCheckCode(b)
        return main == 0 && a.unusual ? a.checkCode - b.checkCode : main;
      });
    const allRejected = this.listRejected.length == checkCodesArray.length;
    if(allRejected){
      this.viewForm.get('penalty').setValue("0");
    }
    this.validationSrv.updateControlReq(this.viewForm.get('basicCode'), !allRejected);
  }

  changeBasicCode($event: any) {
    if(!$event){
      this.rejectBasicCode();
      return;
    }
    if($event.code == '6001') { //если новый основной код 6001, выдаем сообщение об ошибке
      const modal = this.modalService.open(AlertModalComponent);
      modal.componentInstance.text = "Необходимо выбрать код признака необычной операции";
      this.viewForm.get('basicCode').setValue(this.basicCode);
      return;
    }

    const oldBasicCode = this.basicCode;
    this.basicCode = $event.code;
    const codesFormArray = this.viewForm.get('checkCodes') as FormArray;
    const basicCodeItem = codesFormArray.controls.find(c => c.value.checkCode == this.basicCode && !c.value.unusual);
    if(basicCodeItem){ //новый основной код уже был в списке кодов - помечаем его основным, очищаем отклонение, если было
      basicCodeItem.get('basic').setValue(true);
      if(basicCodeItem.value.rejected){ //если новый основной код был отклонен, возвращаем его и очищаем причину отклонения
          this.revertRejected(basicCodeItem);
          this.updateRejected();
      }
    } else { //нового основного кода не было в списке кодов - добавляем новую запись
      let newCode: CheckTransactionCode = {
        checkCode: this.basicCode,
        basic: true,
        unusual: false,
        riskClass: {
          id: this.basicCode,
          type: "FZ115"
        }
      };
      codesFormArray.push(this.service.createCodeForm(newCode));
    }

    const oldBasicCodeItem = codesFormArray.controls.find(c =>
      c.value.checkCode == oldBasicCode || c.value.unusual && c.value.basic);
    if(oldBasicCodeItem){
      oldBasicCodeItem.get('basic').setValue(false);
    }

    this.updateOtherCodes();
  }

  addOtherCode($event: any) {
    const otherCodes = this.viewForm.get('otherCodes');
    let otherCodeValue: string[] = otherCodes.value;

    //если новый код 6001, выдаем сообщение об ошибке
    if(this.basicCode == $event.code || $event.code == '6001') {
      const modal = this.modalService.open(AlertModalComponent);
      modal.componentInstance.text = $event.code == '6001' ? "Необходимо выбрать код признака необычной операции"
        : "Коды не должны дублироваться";
      otherCodeValue.splice(otherCodes.value.indexOf($event.code), 1)
      otherCodes.setValue(otherCodeValue);
      return;
    }
    if(!this.basicCode || this.basicCode == '6001'){
      otherCodeValue.splice(otherCodes.value.indexOf($event.code), 1)
      otherCodes.setValue(otherCodeValue);
      this.viewForm.get('basicCode').setValue($event.code);
      this.changeBasicCode($event);
      return;
    }
    const codesFormArray = this.viewForm.get('checkCodes') as FormArray;
    const addedCodeItem = codesFormArray.controls.find(c => c.value.checkCode == $event.code);

    if(addedCodeItem){
      if(addedCodeItem.value.rejected){
        this.revertRejected(addedCodeItem);
        this.updateRejected();
      }
    } else {
      let newCode: CheckTransactionCode = {
        checkCode: $event.code,
        basic: false,
        unusual: false,
        riskClass: {
          id: $event.code,
          type: "FZ115"
        }
      };
      codesFormArray.push(this.service.createCodeForm(newCode));
    }
  }

  addUnusualCode($event: any) {
    const codesFormArray = this.viewForm.get('checkCodes') as FormArray;
    const addedCodeItem = codesFormArray.controls.find(c =>
      c.value.checkCode == $event.code && c.value.unusual);
    if(addedCodeItem){
      if(addedCodeItem.value.rejected){
        this.revertRejected(addedCodeItem);
        this.updateRejected();
      }
    } else {
      let newCode: CheckTransactionCode = {
        checkCode: $event.code,
        basic: false,
        unusual: true,
        riskClass: {
          id: $event.code,
          type: "P375"
        }
      };
      codesFormArray.push(this.service.createCodeForm(newCode));

    }
    this.updateOtherCodes();
    if(!this.basicCode){
      this.setNewBasicCode()
    }
  }

  private updateOtherCodes(){
    const codesFormArray = this.viewForm.get('checkCodes') as FormArray;
    const checkCodesValue = codesFormArray.value as CheckTransactionCode[];
    this.viewForm.get('otherCodes').setValue([...new Set(checkCodesValue.filter(c =>
      !c.rejected && !c.basic && (this.basicCode != '6001' || !c.unusual))
      .map(c => c.unusual ? '6001' : c.checkCode).sort())]);
  }

  private getMainCheckCode(item: CheckTransactionCode): number {
    return item.unusual ? 6001 : item.checkCode;
  }

  private updateUnusualCodes(){
    const codesFormArray = this.viewForm.get('checkCodes') as FormArray;
    const checkCodesValue = codesFormArray.value as CheckTransactionCode[];
    this.viewForm.get('unusualCodes').setValue(checkCodesValue
      .filter(c => !c.rejected && !c.basic && c.unusual).map(c => c.checkCode).sort());
  }

  private setNewBasicCode(){
    const otherCodes = this.viewForm.get('otherCodes');
    let otherCodeValue: string[] = otherCodes.value;
    if(otherCodeValue?.length > 0) {
      otherCodeValue = otherCodeValue.sort();
      let newBasic = otherCodeValue[0];
      if(newBasic == '6001' && otherCodeValue?.length > 1){
        newBasic = otherCodeValue[1];
      }
      otherCodeValue.splice(otherCodeValue.indexOf(newBasic), 1);
      otherCodes.setValue(otherCodeValue);
      this.viewForm.get('basicCode').setValue(newBasic);
      this.basicCode = newBasic;
      const codesFormArray = this.viewForm.get('checkCodes') as FormArray;
      const newBasicCodeItem = codesFormArray.controls.find(c =>
        newBasic == '6001' ? c.value.unusual : c.value.checkCode == newBasic);
      newBasicCodeItem.get('basic').setValue(true);
    } else {
      this.basicCode = null;
    }
  }

  private onRejectCode6001(){
    if(this.basicCode == "6001") {
      this.setNewBasicCode();
    } else {
      const otherCodes = this.viewForm.get('otherCodes');
      let otherCodeValue: string[] = otherCodes.value;
      const otherCodeIndex = otherCodeValue.indexOf("6001");
      if(otherCodeIndex != -1) {
        otherCodeValue.splice(otherCodeIndex, 1);
        otherCodes.setValue(otherCodeValue);
      }
    }
  }

  getCheckCodeName(code: string): string {
    return this.dictSrv.getDictNameByCode(code, this.dictionary.qrefVo4936);
  }

  onResultRevert(codeInfo: AbstractControl) {
    if(codeInfo.value.rejected){ //если новый основной код был отклонен, возвращаем его и очищаем причину отклонения
      this.revertRejected(codeInfo, false);
      this.updateRejected();
      if(codeInfo.value.unusual){
        this.updateUnusualCodes();
      } else {
        this.updateOtherCodes();
      }
    }
  }

  protected canGenerateFes(): boolean {
    return !(this.formCheck.fesName || !this.formCheck.result || !this.formCheck.basicCode || this.viewForm.dirty || this.viewForm.invalid || this.disabled) ;
  }

  ngOnDestroy(): void {
    if(!this.isReadOnlyAccess){
      this.service.stopEditCheckTransaction(this.id);
    }
    this.$destroy.next();
    this.$destroy.complete();
  }
}
